import React, {useMemo, useState} from 'react';

import {Heading, Icon, Skeleton, Tab} from '@albert-io/atomic';
import ManageRosterContext from 'client/Classrooms/Classroom/Teacher/ManageRoster/ManageRoster.context';
import manageRosterModalCollection from 'client/Classrooms/Classroom/Teacher/ManageRoster/manageRosterModalCollection';
import PeopleContext from 'client/Classrooms/Classroom/Teacher/People/people.context';

import {ErrorRetryState} from '../../components/ErrorRetryState';
import {useClassContentRevealed, useReportClassContentReady} from '../useClassContentReady';
import {StudentsTab} from './StudentsTab';
import type {StudentSegment} from './StudentsTab';
import {TeachersTab} from './TeachersTab';
import {usePeopleRoster} from './usePeopleRoster';

type PeopleTab = 'students' | 'teachers';

// Matches the loaded layout (57px tab bar, "Students" heading, controls, ~69px roster rows) so the
// reveal fills in place.
const PeopleSkeleton = () => (
  <div aria-busy='true' className='flex flex-col'>
    <span className='sr-only' role='status'>
      Loading roster…
    </span>
    <nav
      aria-hidden='true'
      className='-mx-x4 border-border-primary gap-x4 px-x4 py-x2 flex border-b'
    >
      <Skeleton className='h-x3 w-x12 rounded-full' />
      <Skeleton className='h-x3 w-x12 rounded-full' />
    </nav>
    <div className='gap-x3 pt-x4 flex flex-col'>
      <Heading as='h2' className='m-x0' size='s'>
        Students
      </Heading>
      <div aria-hidden='true' className='gap-x2 flex flex-wrap items-center justify-between'>
        <Skeleton className='h-x5 w-[200px] max-w-full rounded-full' />
        <div className='gap-x1 flex items-center'>
          <Skeleton className='h-x5 w-x16 rounded-x1' />
          <Skeleton className='h-x5 w-[150px] rounded-full' />
        </div>
      </div>
      <div aria-hidden='true' className='flex flex-col'>
        {['r1', 'r2', 'r3', 'r4', 'r5'].map((key) => (
          <div className='gap-x2 py-x3 border-border-subtle flex items-center border-b' key={key}>
            <Skeleton className='h-x2 w-[18%] rounded-full' />
            <Skeleton className='h-x2 w-[18%] rounded-full' />
            <Skeleton className='h-x2 grow rounded-full' />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// The reused legacy roster modals/dropdowns read these untyped contexts.
const TypedManageRosterContext = ManageRosterContext as unknown as React.Context<unknown>;
const TypedPeopleContext = PeopleContext as unknown as React.Context<unknown>;
// email | bulkResend | classCode | googleClassroom | LMS — student invite flows, rendered on demand.
const ROSTER_MODALS = manageRosterModalCollection as Record<
  string,
  React.ComponentType<{handleClose: () => void}>
>;

interface Props {
  classroomId: string;
}

export const People = ({classroomId}: Props) => {
  const roster = usePeopleRoster(classroomId);
  const [activeTab, setActiveTab] = useState<PeopleTab>('students');
  const [studentSegment, setStudentSegment] = useState<StudentSegment>('enrolled');
  // Drives the shared student-invite modals (mirrors the legacy ManageRoster container).
  const [modalToShow, setModalToShow] = useState('');

  const {
    classroom,
    isReady,
    hasError,
    reload,
    softReload,
    queries,
    currentTeacherId,
    students,
    invitations
  } = roster;

  // setIsReady(false) is how the reused flows signal "I changed data". Refresh in place (softReload)
  // rather than reload(): reload() drops isReady and remounts the whole view behind a spinner — a
  // jarring "page refresh" even when nothing changed (e.g. cancelling the invite-by-email modal).
  const manageRosterContext = useMemo(
    () => ({
      queries: {
        classroom: queries.classroom,
        invitations: queries.invitations,
        students: queries.students
      },
      classroom,
      invitations,
      students,
      isReady,
      modalToShow,
      setIsReady: (value: boolean) => {
        if (!value) {
          softReload();
        }
      },
      setModalToShow
    }),
    [queries, classroom, invitations, students, isReady, modalToShow, softReload]
  );

  const peopleContext = useMemo(
    () => ({classroom, queries: {coteachers: queries.coteachers}, currentTeacherId}),
    [classroom, queries, currentTeacherId]
  );

  // Report when the roster is ready (or errors); the shell reveals the header + this tab together on
  // `revealed`, so neither shows before the other. The `!isReady` keeps the tab's own skeleton for
  // post-reveal refetches (e.g. a reload() that flips isReady), where the header stays put.
  const revealed = useClassContentRevealed();
  useReportClassContentReady(isReady);

  if (!revealed || !isReady) {
    return <PeopleSkeleton />;
  }

  if (hasError || typeof classroom?.getId !== 'function') {
    return (
      <div className='pt-x4'>
        <ErrorRetryState copy='We couldn’t load this class’s roster.' onRetry={reload} />
      </div>
    );
  }

  const ModalRenderer = modalToShow ? ROSTER_MODALS[modalToShow] : null;

  return (
    <TypedManageRosterContext.Provider value={manageRosterContext}>
      <TypedPeopleContext.Provider value={peopleContext}>
        <div className='flex flex-col'>
          <nav
            aria-label='People views'
            className='-mx-x4 border-border-primary px-x4 flex border-b'
          >
            <Tab
              as='button'
              isActive={activeTab === 'students'}
              onClick={() => setActiveTab('students')}
            >
              <Icon className='mr-x1' icon='users' iconStyle='regular' size='inherit' />
              Students
            </Tab>
            <Tab
              as='button'
              isActive={activeTab === 'teachers'}
              onClick={() => setActiveTab('teachers')}
            >
              <Icon className='mr-x1' icon='person-chalkboard' iconStyle='regular' size='inherit' />
              Teachers
            </Tab>
          </nav>
          {activeTab === 'students' ? (
            <StudentsTab
              classroom={classroom}
              invitations={invitations}
              onSegmentChange={setStudentSegment}
              onShowModal={setModalToShow}
              segment={studentSegment}
              students={students}
            />
          ) : (
            <TeachersTab coteachers={roster.coteachers} currentTeacherId={currentTeacherId} />
          )}
        </div>
        {ModalRenderer && <ModalRenderer handleClose={() => setModalToShow('')} />}
      </TypedPeopleContext.Provider>
    </TypedManageRosterContext.Provider>
  );
};
